import { Check, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { kidColor } from '../lib/palette';
import { api } from '../lib/api';
import type { FamilySchedule, ScheduleBlock } from '../lib/types';
import { Section } from '../ui/Collapsible';
import { useParent } from './ParentContext';
import { sameBlocks, WeekEditor } from './ScheduleEditor';

const names = (xs: string[]) => (xs.length <= 2 ? xs.join(' and ') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);

/**
 * Settings card: the family school schedule (#248). Set the week once and push it to your students; each child keeps
 * their own copy (still editable on their Schedule tab), and children you add later start on it.
 */
export function FamilyScheduleSettings({ embedded = false }: { embedded?: boolean } = {}) {
  const { parent, refresh, toast } = useParent();
  const [fam, setFam] = useState<FamilySchedule | null>(null);
  const [blocks, setBlocks] = useState<ScheduleBlock[]>([]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const load = (f: FamilySchedule) => {
    setFam(f);
    setBlocks(f.blocks);
    setChosen(new Set(f.children.map((c) => c.id)));
  };
  useEffect(() => {
    api
      .get<FamilySchedule>('/api/family-schedule')
      .then(load)
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);

  if (!fam) return null;
  const changed = !fam.set || !sameBlocks(blocks, fam.blocks);
  const targets = fam.children.filter((c) => chosen.has(c.id));
  // Something to do: a new or edited schedule, or a chosen child who isn't on it yet.
  const canSave = changed || targets.some((c) => !c.matches);

  function toggle(id: string) {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChosen(next);
  }

  async function save() {
    const bad = blocks.find((b) => b.end <= b.start);
    if (bad) return toast({ text: 'A school block ends before it starts.', tone: 'alert' });
    setBusy(true);
    try {
      const sorted = [...blocks].sort((a, b) => a.day - b.day || a.start - b.start);
      load(await api.put<FamilySchedule>('/api/family-schedule', { blocks: sorted, childIds: targets.map((c) => c.id) }));
      await refresh();
      toast({ text: targets.length ? `Family schedule pushed to ${names(targets.map((c) => c.name))}.` : 'Family schedule saved.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section embedded={embedded} id="family-schedule" title="Family school schedule" icon={<Users size={18} />} summary={fam.set ? scheduleSummary(fam.blocks) : 'Not set'}>
      <p className="muted small">
        Set your school hours once and push them to your students. Each child keeps their own copy, which you can still change on their Schedule tab, and
        children you add later start on it. Times are in {parent.timezone.replace(/_/g, ' ')}.
      </p>
      <WeekEditor blocks={blocks} onChange={setBlocks} />
      {fam.children.length > 0 && (
        <div className="stack">
          <span className="field-label">Push to</span>
          <div className="family-push-list" role="group" aria-label="Push to">
            {fam.children.map((c) => {
              const on = chosen.has(c.id);
              return (
                <button key={c.id} type="button" className={`chip ${on ? 'active' : ''}`} aria-pressed={on} onClick={() => toggle(c.id)}>
                  {on ? <Check size={14} /> : <span className="dot" style={{ background: kidColor(c.color) }} />}
                  {c.name}
                  {c.matches && !changed && <span className="muted small">· on it</span>}
                </button>
              );
            })}
          </div>
          <p className="muted small">Pushing replaces their weekly school hours. Their yearly pause stays as it is.</p>
        </div>
      )}
      <div className="save-bar">
        <button className="btn primary" disabled={busy || !canSave} onClick={save}>
          {busy ? 'Saving…' : !canSave ? 'Saved' : targets.length ? `Save and push to ${targets.length === 1 ? targets[0]!.name : `${targets.length} students`}` : 'Save family schedule'}
        </button>
      </div>
    </Section>
  );
}

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const time = (m: number) => new Date(2000, 0, 1, Math.floor(m / 60), m % 60).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
/** The fold's summary (#601): "Mon–Fri, 8:00 AM – 2:30 PM", or how many blocks when the days differ. */
export function scheduleSummary(blocks: ScheduleBlock[]): string {
  if (!blocks.length) return 'No school hours';
  const days = [...new Set(blocks.map((b) => b.day))].sort((a, b) => a - b);
  const run = days.every((d, i) => i === 0 || d === days[i - 1]! + 1);
  const dayText = run && days.length > 2 ? `${DAY[days[0]!]}–${DAY[days[days.length - 1]!]}` : days.map((d) => DAY[d]).join(', ');
  const one = blocks.length === days.length && blocks.every((b) => b.start === blocks[0]!.start && b.end === blocks[0]!.end);
  return one ? `${dayText}, ${time(blocks[0]!.start)} – ${time(blocks[0]!.end)}` : `${dayText} · ${blocks.length} blocks a week`;
}
